import classnames from 'classnames';
import { ReactNode } from 'react';
import styles from './grid-item.component.module.less';

type SpanBreakpoint = 'mobile' | 'tablet' | 'desktop';
type PositionBreakpoint = 'mobile' | 'tablet' | 'desktop';

type GridRowValue = number | 'auto';

type ResponsiveSpan = number | Partial<Record<SpanBreakpoint, number>>;
type ResponsiveColumnStart =
  | number
  | Partial<Record<PositionBreakpoint, number>>;
type ResponsiveGridRow =
  | GridRowValue
  | Partial<Record<PositionBreakpoint, GridRowValue>>;

interface GridItemComponentProps {
  children: ReactNode | ReactNode[];
  span?: ResponsiveSpan;
  columnStart?: ResponsiveColumnStart;
  gridRow?: ResponsiveGridRow;
  className?: string;
}

export const gridItemBoundedClassName = styles.bounded;
export const gridItemContentClassName = styles.content;
export const gridItemBoundedContentClassName = styles['content-bounded'];

export const getGridItemContentClassName = (options?: { bounded?: boolean }) =>
  classnames(
    gridItemContentClassName,
    options?.bounded === false ? undefined : gridItemBoundedContentClassName,
  );

const isResponsiveConfig = <T,>(
  value: T | Partial<Record<string, T>> | undefined,
): value is Partial<Record<string, T>> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getRowClassSuffix = (value: GridRowValue) =>
  value === 'auto' ? 'auto' : value;

const GridItem = ({
  children,
  span = 12,
  columnStart,
  gridRow,
  className,
}: GridItemComponentProps) => {
  if (!children) {
    return null;
  }

  const spanClasses: string[] = [];
  const columnStartClasses: string[] = [];
  const gridRowClasses: string[] = [];

  const registerSpanClass = (prefix: string, value?: number) => {
    if (value !== undefined) {
      const classNameValue = styles[`${prefix}-${value}`];
      if (classNameValue) {
        spanClasses.push(classNameValue);
      }
    }
  };

  const registerColumnStartClass = (prefix: string, value?: number) => {
    if (value !== undefined) {
      const classNameValue = styles[`${prefix}-${value}`];
      if (classNameValue) {
        columnStartClasses.push(classNameValue);
      }
    }
  };

  const registerGridRowClass = (prefix: string, value?: GridRowValue) => {
    if (value !== undefined) {
      const suffix = getRowClassSuffix(value);
      const classNameValue = styles[`${prefix}-${suffix}`];
      if (classNameValue) {
        gridRowClasses.push(classNameValue);
      }
    }
  };

  if (isResponsiveConfig<number>(span)) {
    registerSpanClass('span', span.mobile);
    registerSpanClass('span-tablet', span.tablet);
    registerSpanClass('span-desktop', span.desktop);
  } else {
    registerSpanClass('span', span as number);
  }

  if (isResponsiveConfig<number>(columnStart)) {
    registerColumnStartClass('column-start', columnStart.mobile);
    registerColumnStartClass('column-start-tablet', columnStart.tablet);
    registerColumnStartClass('column-start-desktop', columnStart.desktop);
  } else if (typeof columnStart === 'number') {
    registerColumnStartClass('column-start', columnStart);
  }

  if (isResponsiveConfig<GridRowValue>(gridRow)) {
    registerGridRowClass('grid-row-mob', gridRow.mobile);
    registerGridRowClass('grid-row-tablet', gridRow.tablet);
    registerGridRowClass('grid-row-desk', gridRow.desktop);
  } else if (gridRow !== undefined) {
    registerGridRowClass('grid-row-mob', gridRow as GridRowValue);
  }

  return (
    <div
      className={classnames(
        styles.gridItem,
        spanClasses,
        columnStartClasses,
        gridRowClasses,
        className,
      )}>
      {children}
    </div>
  );
};

export default GridItem;
